<script setup>
import Notification from "@/utils/plugins/NotificationService";
import HeaderTitle from "@/components/layout/HeaderTitle.vue";
// eslint-disable-next-line no-unused-vars
import SharedPresence from "@/components/shared/SharedPresence";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import ProjectsService from "@/api/services/projects.service";
import TimeEntryTagService from "@/api/services/timeEntryTag.service";
import { useAuthStore } from "@/app/stores/authStore";
import { usePresenceStore } from "@/app/stores/presenceStore";
import { useUiStore } from "@/app/stores/uiStore";
import { MoreFilled } from "@element-plus/icons-vue";
import TrackerService from "@/api/services/tracker.service";
import TrackerProjectInfo from "@/components/shared/TrackerProjectInfo.vue";
import moment from "moment";
import TrackerWeek from "@/components/shared/TrackerWeek.vue";
import TrackerCurrentEntry from "@/components/shared/TrackerCurrentEntry.vue";

import TrackerProjectSearch from "@/components/shared/TrackerProjectSearch.vue";
import { isDate } from "lodash";
import { formatDate } from "@/utils/dateFormat";

const authStore = useAuthStore();
const presenceStore = usePresenceStore();
const uiStore = useUiStore();
const PROJECT_API = "/api/projects/";
const TAGS_API = "/api/time_entry_tags/";

const timeEntry = ref({
  description: null,
  project: null,
  tags: [],
  timeStart: new Date(),
  timeEnd: new Date(),
  billable: true,
});
const page = ref(1);
const pageTimeEntries = ref(1);
const itemsPerPage = ref(9999);
const itemsPerPageTimeEntries = ref(50);
const totalEntries = ref(10);
const archived = ref(false);
const loadingData = ref(false);

const optionProjects = ref([]);
const optionTags = ref([]);
const entries = ref([]);
const currentEntry = ref(null);
const newDuration = ref(0);
const newDescription = ref(null)

const groupEntriesByWeek = ref([]);
const isWeek = ref(false);

const isManual = ref(false);
const manualEntry = ref({
  timeStart: new Date(),
  timeEnd: new Date(),
})
const refInputStartTime = ref();
const refInputEndTime = ref();
const inputStartTime = ref('');
const inputEndTime = ref('');
const daysDiff = ref(0)

const dropdownProject = ref(false);
const refDropdownProject = ref();
const refEntryModeDropdown = ref();
const refSelectTag = ref();

const popperOptions = {
  modifiers: [
    {
      name: "offset",
      options: {
        offset: [0, 8],
      },
    },
  ],
};

// Autocomplete Description
const refAutocompleteDescription = ref();
const entriesOptions = ref([]);

// Límite de caracteres para descripción
const MAX_DESCRIPTION_LENGTH = 255;
const descriptionLength = computed(() => {
  return timeEntry.value.description ? timeEntry.value.description.length : 0;
});

const showCharCounter = computed(() => {
  return descriptionLength.value > 200;
});

const charCounterClass = computed(() => {
  if (descriptionLength.value >= MAX_DESCRIPTION_LENGTH) {
    return 'char-counter-limit';
  } else if (descriptionLength.value >= 240) {
    return 'char-counter-warning';
  } else {
    return 'char-counter-info';
  }
});

const currentEntryRef = ref(null);

const user = computed(() => authStore.getUserLogged);

const canAccessTrackernet = computed(() => {
  return !!user.value?.canCreateTimeEntry;
})

const setDuration = () => {
  const duration = parseInt(newDuration.value, 10);
  if (!isNaN(duration)) {
    presenceStore.setEntryDuration(duration);
  }
}

const startInterval = () => presenceStore.startInterval();
const stopInterval = () => presenceStore.stopInterval();
const changeIsEntryActive = (isActive) => presenceStore.setIsEntryActive(isActive);


onMounted(async () => {
  uiStore.startLoading();
  isWeek.value = false;
  stopInterval();
  await getProjects();
  await getTags();
  await getEntries();
  await setManualTime();
  uiStore.stopLoading();
})

onUnmounted(() => {
  stopInterval();
})

const getEntries = () => {
  if (!user.value?.id) {
    loadingData.value = false;
    uiStore.stopLoading();
    return;
  }
  uiStore.startLoading();
  loadingData.value = true;
  timeEntry.value.description = null;
  newDescription.value = '';
  timeEntry.value.billable = true;
  timeEntry.value.project = null;
  timeEntry.value.tags = [];
  timeEntry.value.timeEnd = null;
  timeEntry.value.timeStart = new Date();

  currentEntry.value = null;

  newDuration.value = 0;

  let params = {
    page: pageTimeEntries.value,
    itemsPerPage: itemsPerPageTimeEntries.value,
    'order[timeStart]': 'DESC'
  }

  let temp = 0;

  TrackerService.getEmployeeEntries(user.value.id, params).then((response) => {
    if (!user.value) return;
    entries.value = response.data['hydra:member'];
    totalEntries.value = response.data['hydra:totalItems'];
    entries.value.map((entry) => {
      if (!entry.timeEnd) {
        currentEntry.value = entry;
        setTotalTime();
        let total = currentEntry.value.totalTime.hours * 3600;
        total += currentEntry.value.totalTime.minutes * 60;
        total += currentEntry.value.totalTime.seconds;
        currentEntry.value.duration = total;
        newDuration.value = currentEntry.value.duration;
        temp++;
        if (!presenceStore.isEntryActive) {
          startInterval();
        }
      }
    })
  }).catch(() => {
    uiStore.stopLoading();
    loadingData.value = false;
  }).finally(() => {
    setDuration();
    if (!presenceStore.isEntryActive && temp > 0) {
      changeIsEntryActive(true);
    }
    if (temp === 0 && presenceStore.isEntryActive) {
      stopInterval();
    }
    presenceStore.setEntryDuration(newDuration.value);
    groupEntriesByWeek.value = getGroupedEntriesByWeek();
    isWeek.value = true;
    setTimeout(() => {
      loadingData.value = false;
      uiStore.stopLoading();
    }, 100);
    setManualTime();
  })
}

const setTotalTime = () => {
  currentEntry.value.totalTime = calcularTiempoTarea(currentEntry.value.timeStart);
}

const calcularTiempoTarea = (fecha) => {
  let time = new Date(fecha)
  const now = new Date();
  const diferenciaMs = now - time;
  if (diferenciaMs < 0) {
    throw new Error("La fecha proporcionada es  futura, no se puede calcular el tiempo transcurrido.");
  }
  const segundosTotales = Math.floor(diferenciaMs / 1000);
  const hours = Math.floor(segundosTotales / 3600);
  const minutes = Math.floor((segundosTotales % 3600) / 60);
  const seconds = segundosTotales % 60;
  return {
    hours,
    minutes,
    seconds,
  };
}

// Getters Projects and Tags

const getProjects = () => {
  let params = {
    page: page.value,
    itemsPerPage: itemsPerPage.value,
    archived: archived.value,
    'order[initialDate]': 'DESC'
  }
  uiStore.startLoading();
  ProjectsService.getProjects(params)
    .then((response) => {
      optionProjects.value = response.data['hydra:member'];
    })
    .catch((error) => {
      console.log(error);
      Notification.addNotification({
        message: "Error al cargar los proyectos",
        type: "error",
        duration: 6000
      });
      uiStore.stopLoading();
    })
    .finally(() => {
      uiStore.stopLoading();
    });
}

const getTags = () => {
  let params = {
    page: page.value,
    itemsPerPage: itemsPerPage.value,
  }
  params['order[name]'] = 'ASC';
  uiStore.startLoading();
  TimeEntryTagService.getTags(params)
    .then((response) => {
      optionTags.value = response.data['hydra:member'];
    })
    .catch((error) => {
      console.log(error);
      Notification.addNotification(
        "Error al cargar las etiquetas"
      );
      uiStore.stopLoading();
    })
    .finally(() => {
      uiStore.stopLoading();
    });

}

// Function Save Entry

const saveTimeEntry = () => {
  if (!user.value?.['@id']) return;
  let tags = [];
  for (let i = 0; i < timeEntry.value.tags.length; i++) {
    const tag = timeEntry.value.tags[i];
    tags.push(TAGS_API + tag.id)
  }

  if (tags.length < 1 || timeEntry.value.project === null || timeEntry.value.description == '' || timeEntry.value.description == null) {
    let mssg = "No se ha podido cerrar la tarea, falta: ";
    const arrayErr = [];
    if (timeEntry.value.description == '' || timeEntry.value.description == null) arrayErr.push('descripcion')
    if (timeEntry.value.project === null) arrayErr.push('proyecto')
    if (tags.length < 1) arrayErr.push('etiquetas')
    for (let i = 0; i < arrayErr.length; i++) {
      if (i == 0) {
        mssg = mssg + arrayErr[0];
      } else {
        mssg = mssg + ', ' + arrayErr[i];
      }
    }
    Notification.addNotification({
      message: mssg,
      type: "error",
      duration: 6000
    })
    return;
  }

  if (timeEntry.value.description.length < 5) {
    Notification.addNotification({
      message: "La descripción tiene que contener al menos 5 caracteres",
      type: "error",
      duration: 6000
    })
    return;
  }
  // Hacer comprobación para empezar la tarea vacia

  const project = timeEntry.value.project ? PROJECT_API + timeEntry.value.project.id : "";
  let entry;
  if (isManual.value) {
    if (!applyManualInputs()) {
      Notification.addNotification({
        message: "Introduce una hora válida",
        type: "error",
        duration: 6000
      })
      return;
    }

    const manualDurationError = getManualDurationError(manualEntry.value.timeStart, manualEntry.value.timeEnd);
    if (manualDurationError) {
      Notification.addNotification({
        message: manualDurationError,
        type: "error",
        duration: 6000
      })
      return;
    }
    entry = {
      timeStart: formatDate(new Date(manualEntry.value.timeStart)),
      timeEnd: formatDate(new Date(manualEntry.value.timeEnd)),
      employee: user.value['@id'],
      project: project,
      tags: tags,
      description: timeEntry.value.description,
      billable: timeEntry.value.billable,
    }
  } else {
    entry = {
      timeStart: formatDate(new Date()),
      employee: user.value['@id'],
      project: project,
      tags: tags,
      description: timeEntry.value.description,
      billable: timeEntry.value.billable,
    }
  }
  TrackerService.createEntry(entry).then((response) => {
    if (isManual.value) {
      getEntries();
      currentEntry.value = null;
      stopInterval();
      return;
    }

    currentEntry.value = response.data;
    getEntries();
    startInterval();
  })
}

// Descripcion // Autocompletar
// Cuando se elige una nueva entrada, hace los cambios y actualiza el autocompletar
const querySearch = (queryString, callback) => {
  // Filtra las entradas basándose en el queryString (valor actual del input)
  if (queryString.length < 3 || queryString == '') {
    callback([]);
    return;
  }
  let params = {
    page: 1,
    itemsPerPage: 9,
    'order[timeStart]': 'DESC',
    description: queryString,
  }

  if (!user.value?.id) {
    callback([]);
    return;
  }

  TrackerService.getEmployeeEntries(user.value.id, params).then((res) => {
    if (!user.value) {
      callback([]);
      return;
    }
    entriesOptions.value = [];
    for (let i = 0; i < res.data['hydra:member'].length; i++) {
      const entry = res.data['hydra:member'][i];
      if (entry.timeEnd) {
        entriesOptions.value.push(entry)
      }
    }
    callback(entriesOptions.value);
  })
};

// Cuando eliges una entrada del autocompletar
const handleSelect = (val) => {
  selectNewEntry(val)
}

const moveDescriptionCursorToEdge = (event) => {
  if (event.key !== 'Home' && event.key !== 'End') return;

  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;

  const position = event.key === 'Home' ? 0 : input.value.length;
  const anchor = input.selectionStart ?? position;
  event.preventDefault();
  event.stopPropagation();
  input.setSelectionRange(event.shiftKey ? anchor : position, position);
}

const selectNewEntry = (entry) => {
  timeEntry.value.description = entry.description;
  newDescription.value = entry.description;
  timeEntry.value.billable = entry.billable;
  timeEntry.value.project = entry.project;
  timeEntry.value.tags = entry.tags;
}

const getTagsText = (tags) => {
  let text = '';
  for (let i = 0; i < tags.length; i++) {
    const element = tags[i];
    if (i == 0) {
      text = element.name;
    } else {
      text = text + ", " + element.name
    }
  }
  return text;
}

// Paginacion

const changePagination = () => {
  getEntries();
}

// Ordenar semanas

const getGroupedEntriesByWeek = () => {
  if (entries.value.length === 0) return []
  const groupedByWeek = {};
  const previousYears = [];
  const currentYear = moment().year();

  entries.value.forEach(entry => {
    const entryMoment = moment(entry.timeStart)
    const weekOfYear = entryMoment.isoWeek();
    const year = moment(entry.timeStart).isoWeekYear();
    const key = `${year}-${weekOfYear}`
    if (!groupedByWeek[key]) {
      groupedByWeek[key] = [];
    }
    entry.number = weekOfYear;
    groupedByWeek[key].push(entry);
    if (year !== currentYear) {
      previousYears.push(entry)
    }
  });

  return Object.keys(groupedByWeek).map(key => {
    const [yearStr, weekStr] = key.split("-");
    const year = parseInt(yearStr);
    const week = parseInt(weekStr);
    return {
      year,
      weekNumber: week,
      week: getWeekStatus(week, year),
      entries: groupedByWeek[key]
    };
  }).sort((a, b) => {
    // Orden REAL por fecha, no solo números
    const aDate = moment().isoWeekYear(a.year).isoWeek(a.weekNumber).startOf("isoWeek");
    const bDate = moment().isoWeekYear(b.year).isoWeek(b.weekNumber).startOf("isoWeek");

    return aDate.diff(bDate);
  });
};

const getWeekStatus = (weekNumber, year) => {
  const now = moment();

  const currentWeek = now.isoWeek();
  const currentYear = now.isoWeekYear();

  if (year === currentYear) {
    if (weekNumber === currentWeek) {
      return "Semana Actual";
    }
    if (weekNumber === currentWeek - 1) {
      return "Semana Anterior";
    }
    if (weekNumber === currentWeek + 1) {
      return "Semana Siguiente";
    }
  }

  return getWeekRange(weekNumber, year);
};

const getWeekRange = (weekNumber, year) => {
  const startOfWeek = moment().year(year).week(weekNumber).startOf('week');
  const endOfWeek = moment(startOfWeek).endOf('week');
  const currentYear = moment().year();
  let formatOptions;
  if (year != currentYear) {
    formatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric"
    };
  } else {
    formatOptions = {
      day: "numeric",
      month: "long"
    };
  }

  const startDate = startOfWeek.toDate().toLocaleDateString("es-ES", formatOptions);
  const endDate = endOfWeek.toDate().toLocaleDateString("es-ES", formatOptions);

  return `${capitalizeTitle(startDate)} - ${capitalizeTitle(endDate)}`;
};

const capitalizeTitle = (text) => {
  const prepositions = ["de", "la", "el", "en", "y", "con", "por", "a", "del", "los", "las", "al", "un", "una", "unos", "unas", "para", "sobre"];

  return text
    .toLowerCase()
    .split(" ")
    .map((word, index) =>
      prepositions.includes(word) && index !== 0
        ? word // No cambia preposiciones (excepto si es la primera palabra)
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");
}

// Manual Timer

const normalizeManualDate = (date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setSeconds(0, 0);
  return normalizedDate;
}

const buildManualDate = (baseDate, hours, minutes, dayOffset = 0) => {
  const date = new Date(baseDate || new Date());
  date.setDate(date.getDate() + dayOffset);
  date.setHours(hours, minutes, 0, 0);
  return date;
}

const buildManualDateFromExistingTime = (date, dayOffset = 0) => {
  const sourceDate = new Date(date);
  return buildManualDate(day.value, sourceDate.getHours(), sourceDate.getMinutes(), dayOffset);
}

const parseManualTimeInput = (value, baseDate = day.value) => {
  const timeValue = String(value || '').trim();
  let hours;
  let minutes;

  if (/^\d{1,2}:\d{1,2}$/.test(timeValue)) {
    const parts = timeValue.split(':');
    hours = Number(parts[0]);
    minutes = Number(parts[1]);
  } else if (/^\d{3,4}$/.test(timeValue)) {
    const hourDigits = timeValue.length === 3 ? timeValue.slice(0, 1) : timeValue.slice(0, 2);
    const minuteDigits = timeValue.length === 3 ? timeValue.slice(1) : timeValue.slice(2);
    hours = Number(hourDigits);
    minutes = Number(minuteDigits);
  } else if (/^\d{1,2}$/.test(timeValue)) {
    hours = Number(timeValue);
    minutes = 0;
  } else {
    return false;
  }

  if (Number.isNaN(hours) || Number.isNaN(minutes) || hours > 23 || minutes > 59) {
    return false;
  }

  return buildManualDate(baseDate, hours, minutes);
}

const syncManualInputs = () => {
  inputStartTime.value = moment(manualEntry.value.timeStart).format('HH:mm');
  inputEndTime.value = moment(manualEntry.value.timeEnd).format('HH:mm');
  daysDiff.value = getDaysDifference(manualEntry.value.timeStart, manualEntry.value.timeEnd);
}

const applyManualInputs = () => {
  const newStart = parseManualTimeInput(inputStartTime.value);
  const newEndBase = parseManualTimeInput(inputEndTime.value);

  if (newStart === false || newEndBase === false) {
    syncManualInputs();
    return false;
  }

  manualEntry.value = {
    timeStart: newStart,
    timeEnd: newEndBase < newStart ? buildManualDateFromExistingTime(newEndBase, 1) : buildManualDateFromExistingTime(newEndBase)
  }
  syncManualInputs();
  return true;
}

const setManualTime = () => {
  const now = normalizeManualDate(new Date());
  day.value = new Date(now);
  manualEntry.value = {
    timeStart: new Date(now),
    timeEnd: new Date(now),
  }
  syncManualInputs();
}

const setEntryManual = () => {
  closeEntryModeDropdown();
  isManual.value = true;
}

const setEntryTimer = () => {
  closeEntryModeDropdown();
  isManual.value = false;
}

const closeEntryModeDropdown = () => {
  if (typeof document !== "undefined") {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && activeElement.closest(".el-dropdown__popper")) {
      activeElement.blur();
    }
  }
  refEntryModeDropdown.value?.handleClose?.();
}

const focusInputHour = (event) => {
  if (refInputStartTime.value?.input === event.target) {
    inputStartTime.value = inputStartTime.value.split(':');
    let temp = inputStartTime.value[0] + inputStartTime.value[1];
    inputStartTime.value = temp;
    nextTick(() => {
      refInputStartTime.value.select();
    });
  } else if (refInputEndTime.value?.input === event.target) {
    inputEndTime.value = inputEndTime.value.split(':');
    let temp = inputEndTime.value[0] + inputEndTime.value[1];
    inputEndTime.value = temp;
    nextTick(() => {
      refInputEndTime.value.select();
    });
  }
}

const blurInputHour = (event) => {
  if (refInputStartTime.value?.input === event.target) {
    changeStartInput();
  } else if (refInputEndTime.value?.input === event.target) {
    changeEndInput();
  }
}

const checkChangeInputHour = (input) => {
  return parseManualTimeInput(input);
}

const changeStartInput = () => {
  const newH = checkChangeInputHour(inputStartTime.value)
  if (newH === false) {
    syncManualInputs();
    return;
  }

  const newEndBase = parseManualTimeInput(inputEndTime.value) || buildManualDateFromExistingTime(manualEntry.value.timeEnd);
  let newEnd = newEndBase;

  if (newH > newEndBase) {
    newEnd = buildManualDateFromExistingTime(newEndBase, 1);
  } else {
    newEnd = buildManualDateFromExistingTime(newEndBase);
  }

  manualEntry.value.timeStart = newH;
  manualEntry.value.timeEnd = newEnd;
  syncManualInputs();

}

const changeEndInput = () => {
  const newH = checkChangeInputHour(inputEndTime.value)
  if (newH === false) {
    syncManualInputs();
    return;
  }

  const newStart = parseManualTimeInput(inputStartTime.value) || buildManualDateFromExistingTime(manualEntry.value.timeStart);
  let newEnd = newH;

  if (newH < newStart) {
    newEnd = buildManualDateFromExistingTime(newH, 1);
  } else {
    newEnd = buildManualDateFromExistingTime(newH);
  }

  manualEntry.value.timeStart = newStart;
  manualEntry.value.timeEnd = newEnd;
  syncManualInputs();
}

const getManualDurationError = (timeStart, timeEnd) => {
  const start = moment(timeStart);
  const end = moment(timeEnd);
  if (!start.isValid() || !end.isValid()) return "Introduce una hora válida";
  if (!start.isBefore(end)) return "La hora de fin debe ser posterior a la hora de inicio";
  const hours = end.diff(start, 'hours', true);
  if (hours > 8) return "La duración no puede ser superior a 8 horas";
  return null;
}

// Proyecto
const getNewProject = (newProject) => {
  if (timeEntry.value.project?.id === newProject?.id) {
    timeEntry.value.project = null;
  } else {
    timeEntry.value.project = newProject;
  }
  refDropdownProject.value.handleClose();
}

const isDropdownOpen = ref(false);

const onDropdownToggle = (isOpen) => {
  isDropdownOpen.value = isOpen;
}

const clearSelectTag = () => {
  if (refSelectTag.value) {
    refSelectTag.value.blur(); // Cierra el dropdown y limpia el input de búsqueda
    refSelectTag.value.focus(); // Vuelve a enfocar para que siga funcional
  }
}

// Stop and start entry

const newEntry = ref(null);
const restartNewEntry = ref(false);

const restartEntry = (entry) => {
  newEntry.value = entry;
  if (newEntry.value) {
    stopProjectStarted();
  }
}

// eslint-disable-next-line no-unused-vars
const stopProjectStarted = async () => {
  if (currentEntry.value) {
    if (newEntry.value) {
      restartNewEntry.value = true;
      await nextTick();
      await startTimer();
      restartNewEntry.value = false;
    }
  }
}

const startTimer = async () => {
  let entry = { ...newEntry.value }
  entry.project = newEntry.value.project['@id'];
  entry.employee = newEntry.value.employee['@id'];
  let tags = [];
  entry.tags.forEach((tag) => {
    tags.push(tag['@id']);
  });

  delete entry['@id'];
  delete entry.id;
  delete entry.duration;
  delete entry.timeEnd;
  entry.tags = tags;
  entry.timeStart = formatDate(new Date());

  try {
    await TrackerService.createEntry(entry);
    await getEntries();
  } catch {
    return;
  }
}

// Calendar

const datePicker = ref();
const day = ref(new Date());

const isToday = (date) => {
  const today = new Date()
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
}

// eslint-disable-next-line no-unused-vars
const dateText = computed(() => {
  if (!day.value) return ''

  if (isToday(day.value)) {
    return 'Hoy'
  }

  const dd = String(day.value.getDate()).padStart(2, '0')
  const mm = String(day.value.getMonth() + 1).padStart(2, '0')
  const yyyy = day.value.getFullYear()

  return `${dd}/${mm}/${yyyy}`
})

const openCalendar = async () => {
  await nextTick();
  if (datePicker.value) {
    datePicker.value.handleOpen();
  }
}

const changeDay = () => {
  if (!day.value) return;

  const currentStart = new Date(manualEntry.value.timeStart);
  const currentEnd = new Date(manualEntry.value.timeEnd);
  const currentDaysDiff = getDaysDifference(currentStart, currentEnd);

  manualEntry.value = {
    timeStart: buildManualDate(day.value, currentStart.getHours(), currentStart.getMinutes()),
    timeEnd: buildManualDate(day.value, currentEnd.getHours(), currentEnd.getMinutes(), currentDaysDiff)
  }
  syncManualInputs();
}

// eslint-disable-next-line no-unused-vars
// Guía estática servida desde public/tracker-help (se abre en pestaña nueva)
const helpUrl = `${process.env.BASE_URL}tracker-help/`;

const getDaysDifference = (startDate, endDate) => {
  if (!isDate(startDate)) startDate = new Date(startDate);
  if (!isDate(endDate)) endDate = new Date(endDate);

  const d1 = moment(startDate).startOf('day');
  const d2 = moment(endDate).startOf('day');

  return Math.abs(d1.diff(d2, 'days'));
}

</script>

<template>
  <div class="tracker-net">
    <HeaderTitle title="Producción" :trackernet="true" />
    <div class="d-flex flex-row justify-content-between align-items-center sticky-title">
      <div class="d-flex flex-row">
        <img src="@/assets/icons/tracker-net-blue.svg" alt="">
        <div class="title-text ml-4">
          TrackerNet
        </div>
      </div>
      <a :href="helpUrl" target="_blank" rel="noopener" class="btn btn_red btn-help" aria-label="Ayuda"
        title="Ayuda">
        <img src="@/assets/icons/help-icon-white.svg" width="25" alt="">
        <span class="help-label">Ayuda</span>
      </a>
    </div>
    <el-row class="mt-2">
      <el-card v-if="currentEntry" class="card-search w-100 sticky-card">
        <tracker-current-entry :entry="currentEntry" @reload-entries="getEntries()" ref="currentEntryRef"
          :restarting="restartNewEntry" />
      </el-card>
      <el-card v-else class="card-search w-100 sticky-card">
        <div class="row">
          <!-- COLUMN 1 -->
          <div class="column1" :class="isManual ? 'col-5' : 'col-6'">
            <div class="row d-flex">
              <!-- DESCRIPTION -->
              <div class="col-8 el-col-xs-24 tracker-description">
                <el-autocomplete ref="refAutocompleteDescription" v-model="timeEntry.description"
                  :fetch-suggestions="querySearch" style="width: 100%;" @select="handleSelect"
                  @keydown="moveDescriptionCursorToEdge" placeholder="Descripción de la tarea" class="inputDescription"
                  :trigger-on-focus="false" popper-class="custom-autocomplete-popper" :title="timeEntry.description"
                  value-key="description" :maxlength="MAX_DESCRIPTION_LENGTH">
                  <template #default="{ item }">
                    <div class="custom-suggestion d-flex fixed-dropdown-item">
                      <span class="ellipsis-span" style="max-width: 40%;" :title="item.description">
                        {{ item.description }}
                      </span>
                      <span class="ellipsis-span" style="max-width: 20%;" :title="item.project.name">
                        <tracker-project-info :project="item.project" :select="true" class="projectSearch" />
                      </span>
                      <span class="ellipsis-span tracker-tag" style="max-width: 40%;" :title="getTagsText(item.tags)">
                        {{ getTagsText(item.tags) }}
                      </span>
                    </div>
                  </template>
                </el-autocomplete>
                <span v-if="showCharCounter" :class="['char-counter', charCounterClass]">
                  {{ descriptionLength }}/{{ MAX_DESCRIPTION_LENGTH }}
                </span>
              </div>
              <!-- PROJECT -->
              <div class="col-4 justify-content-center align-content-center el-col-xs-24 projectResponsive">
                <el-dropdown trigger="click" :hide-on-click="false" ref="refDropdownProject" class="addProjectDropdown"
                  placement="bottom" @visible-change="onDropdownToggle">
                  <p v-if="!timeEntry.project" @click="dropdownProject = true" style="margin-top: 13px;"
                    class="addProjectResponsive">
                    Añadir Proyecto
                  </p>
                  <tracker-project-info :project="timeEntry.project" class="el-dropdown-link"
                    @click="dropdownProject = true" v-if="timeEntry.project" :currentEntry="true" />
                  <template #dropdown>
                    <el-dropdown-menu class="dropProject">
                      <el-dropdown-item style="height: 300px;" v-if="dropdownProject">
                        <TrackerProjectSearch @get-project="getNewProject" :project="timeEntry.project"
                          :dropdown-open="isDropdownOpen" />
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </div>
          </div>
          <!-- COLUMN 2 -->
          <div class="column2" :class="isManual ? 'col-7' : 'col-6'">
            <div class="row d-flex">
              <!-- TAGGS -->
              <div :class="isManual ? 'col-5' : 'col-6'" class="el-col-xs-24">
                <el-select ref="refSelectTag" :value-key="'@id'" clearable filterable multiple collapse-tags
                  collapse-tags-tooltip placeholder="Etiqueta" v-model="timeEntry.tags" popper-class="dropdownEntryTags"
                  class="tracker-select responsive-select" @change="clearSelectTag">
                  <el-option v-for="tag in optionTags" :key="tag['@id']" :label="tag.name" :value="tag">
                  </el-option>
                </el-select>
              </div>
              <!-- BILLABLE -->
              <div class="text-center p-2 el-col-xs-4 trackernet-billable" :class="isManual ? 'col-1' : 'col-4'">
                <el-button class="tracker-button"
                  :class="timeEntry.billable ? 'tracker-button--active' : 'tracker-button--inactive'"
                  @click="timeEntry.billable = !timeEntry.billable" style="background: none;"
                  :style="isManual ? 'margin-left: 5px; padding-left: 0;' : ''">
                  €
                </el-button>
              </div>
              <!-- TIME INPUTS // ADD BUTTON -->
              <div :class="isManual ? 'col-6' : 'col-2'" class="el-col-xs-20">
                <div class="row">
                  <!-- TIME INPUTS (MANUAL TIMER) -->
                  <div class="col-8 tracker-select mt-2 el-col-xs-24" v-if="isManual"
                    style="margin-left: 20px; margin-right: -20px;">
                    <el-row :gutter="10">
                      <el-col :span="6">
                        <el-input ref="refInputStartTime" v-model="inputStartTime" @focus="focusInputHour"
                          @change="changeStartInput()" @blur="blurInputHour" class="inputManual"
                          style="margin-left: -40px;"></el-input>
                      </el-col>
                      <el-col :span="7" class="manual-time-end-col">
                        <div class="manual-time-end">
                          <el-input ref="refInputEndTime" v-model="inputEndTime" @focus="focusInputHour"
                            @change="changeEndInput()" @blur="blurInputHour" class="inputManual">
                          </el-input>
                          <span v-if="daysDiff > 0 && !isNaN(daysDiff)" class="manual-day-offset">
                            +{{ daysDiff }}
                          </span>
                        </div>
                      </el-col>
                      <el-col :span="1" style="margin-left: 5px; margin-right: -6px;">
                        <el-date-picker v-model="day" ref="datePicker" type="date" class="myDatePicker"
                          style="visibility: hidden; max-width: 1px; min-width: 1px; margin-top: 7px; margin-left: 4px; position: absolute;"
                          :teleport="false" popper-class="custom-datepicker-popup" :popper-options="popperOptions"
                          @click="openCalendar" @change="changeDay">
                        </el-date-picker>
                        <img class="tracker-calendar img-date" @click="openCalendar"
                          src="@/assets/icons/calendar-icon.svg" alt="calendar-icon"
                          style="cursor: pointer; margin-top: 3px;" width="21" height="22">
                      </el-col>
                      <el-col :span="2" class="manualDateText">
                        <span @click="openCalendar">
                          {{ dateText }}
                        </span>
                      </el-col>
                    </el-row>
                  </div>
                  <!-- ADD ENTRY -->
                  <div class="p-2 flex-column justify-content-end align-content-end el-col-xs-23 button-add-entry"
                    :class="isManual ? 'col-3 text-end ' : 'col-7 text-end'">
                    <el-button type="primary" class="w-10" @click="saveTimeEntry" :disabled="!canAccessTrackernet"
                      :style="isManual ? 'margin-right: 0px;' : ''">
                      Añadir
                    </el-button>
                  </div>
                  <!-- MENU DROPDOWN -->
                  <div class="col-1 text-center p-2 flex-column justify-content-end el-col-xs-1 trackernet-dropdown-add"
                    style="margin-left: 10px; margin-right: -10px;">
                    <el-dropdown ref="refEntryModeDropdown" class="dropdown">
                      <span class="el-dropdown-link">
                        <el-icon class="el-icon--right" style="height: 30px;">
                          <MoreFilled
                            style="transform: rotate(90deg);color:#1D1D1B80; margin-left: -10px; font-size: 20px;" />
                        </el-icon>
                      </span>
                      <template #dropdown>
                        <el-dropdown-menu>
                          <el-dropdown-item @click="setEntryTimer()">
                            <el-icon>
                              <Clock />
                            </el-icon>
                            Timer
                          </el-dropdown-item>
                          <el-dropdown-item @click="setEntryManual()">
                            <el-icon>
                              <Memo />
                            </el-icon>
                            Manual
                          </el-dropdown-item>
                        </el-dropdown-menu>
                      </template>
                    </el-dropdown>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-card>
      <div v-if="isWeek && !loadingData" style="width: 100%;">
        <tracker-week v-for="entries in groupEntriesByWeek.slice().reverse()" :key="entries.week"
          :entriesWeek="entries.entries" :titleWeek="entries.week" :projects="optionProjects" :tags="optionTags"
          @reload-entries="getEntries" @restartEntry="restartEntry" :canCreateTimeEntry="canAccessTrackernet" />
      </div>
    </el-row>
    <div class="mt-4" v-if="!loadingData">
      <SharedElementPagination v-model:current-page="pageTimeEntries" v-model:page-size="itemsPerPageTimeEntries"
        size="default" :page-size-options="[50, 100, 200]" layout="prev, pager, next, sizes" :total="totalEntries"
        class="tracker-pagination" show-always @change="changePagination" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.tracker-net {
  margin-bottom: 20px;
}

.btn-help {
  width: 230px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.card-search {
  height: auto;
  border-radius: 15px;
  max-height: 300px;
  background: white;
  background-position-x: 100%;
  overflow: visible !important;
}

.tracker {
  width: 100%;
  //border: 0 !important;
  //border-radius: 0 !important;
}

//.el-select__selection {
//  border: none !important;
//  border-radius: 0 !important;
//}

//input{
//  width: 100%;
//  border: none !important;
//  border-radius: 0 !important;
//}

.el-select {
  border: none !important;
  border-radius: 0 !important;

  .el-select__wrapper {
    border: none !important;
    border-radius: 0 !important;
  }
}

.el-select__input {
  border: none !important;
  border-radius: 0 !important;
}

.dropdown {
  margin-left: 20px;
}

.this-week {
  font-size: 20px;
}

.addProjectDropdown {
  margin-left: 10px;
}

.dropdownDescription {
  .el-input__wrapper {
    .el-input__inner {
      cursor: text !important;
    }
  }
}

.tracker-tag {
  background-color: #f7d20e !important;
  color: #474ce6 !important;
  padding-left: 10px;
  padding-right: 10px;
}

.fixed-dropdown-item {
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
  padding: 3px;

  >span {
    // flex: 1 1 auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 8px;
  }
}


:deep(.custom-autocomplete-popper) {
  width: 300px !important;
  max-height: 200px !important;
}

.custom-autocomplete-popper {
  width: 300px !important;
  /* Ajusta el ancho deseado */
  max-height: 200px !important;
  /* Ajusta el alto deseado, o usa height si es fijo */
  /* Puedes agregar otros estilos, como padding, borde, etc. */
}

.tracker-description {
  position: relative;
}

.char-counter {
  font-size: 11px;
  position: absolute;
  right: 10px;
  top: -18px;
  font-weight: 500;
  transition: color 0.3s ease;
}

.char-counter-info {
  color: #909399;
}

.char-counter-warning {
  color: #E6A23C;
}

.char-counter-limit {
  color: #F56C6C;
  font-weight: 600;
}

.manual-time-end-col {
  margin-left: -15px;
}

.manual-time-end {
  position: relative;
  width: fit-content;
}

.manual-day-offset {
  align-items: center;
  background-color: #474ce6;
  border: 2px solid #fff;
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  font-size: 11px;
  font-weight: 700;
  height: 20px;
  justify-content: center;
  min-width: 24px;
  padding: 0 5px;
  position: absolute;
  right: -14px;
  top: -9px;
  z-index: 2;
}

.myDatePicker {
  visibility: hidden;
  max-width: 10px;
  min-width: 10px;
  position: absolute;
  margin-top: 10px;
  margin-left: 4px;
  top: 0;
}

.trackernet-billable {
  @media (min-width: 767px) {
    border-left: 1px solid #3333;
  }

  @media (max-width: 767px) {
    button {
      margin-left: 0px !important;
    }
  }
}

.trackernet-dropdown-add {
  @media (max-width: 767px) {
    text-align: start !important;
    margin-left: -10px !important;
  }
}

.button-add-entry {
  @media (max-width: 767px) {
    button {
      margin-right: 0px !important;
    }
  }
}

@media (min-width: 768px) {
  .responsive-select {
    width: 100%;

    :deep(.el-select__wrapper) {
      min-height: 32px;
      align-items: center;
    }

    :deep(.el-select__selection) {
      display: flex;
      align-items: center;
      flex-wrap: nowrap;
      min-width: 0;
    }

    :deep(.el-select__selected-item) {
      align-items: center;
      flex-shrink: 0;
    }

    :deep(.el-select__input-wrapper) {
      flex: 1 1 auto;
      min-width: 70px;
    }
  }
}

@media (max-width: 767px) {
  .sticky-title {
    width: 100%;
    gap: 8px;

    > div {
      min-width: 0;
      align-items: center;
    }

    > div > img {
      width: 36px;
      flex-shrink: 0;
    }

    .title-text {
      font-size: clamp(24px, 7vw, 32px) !important;
    }
  }

  .btn-help {
    flex: 0 0 44px;
    border-radius: 50% !important;
    width: 44px;
    height: 44px;
    padding: 0;
    margin: 0 0 0 auto;
    display: grid;
    place-items: center;
    gap: 0;
    line-height: 0;

    > img {
      display: block;
      width: 25px;
      height: 25px;
      margin: 0;
      padding: 0;
    }

    .help-label {
      display: none;
    }
  }

  .trackernet-body-title {
    .title-text {
      font-size: 24px !important;
    }

    img {
      width: 30px;
    }
  }

  .el-select__popper {
    width: 66% !important;
  }

  .responsive-select-tag {
    display: none;
    width: 66% !important;
    background-color: red !important;
  }

  .projectResponsive {
    border-radius: 20px;
    padding: 0;
    border: 1px solid black !important;
    margin-top: 8px;
    margin-left: 10px;
    max-width: 95%;
  }

  .el-dropdown-link {
    margin-top: 8px;
    margin-bottom: 10px;
  }
}
</style>
